/**
 * Label deconfliction for map markers.
 * Determines which marker labels should be visible based on severity priority
 * and spatial overlap avoidance.
 */

import type { Severity } from '@/sources/base-source';
import { SEVERITY_RANK } from '@/utils/severity';

/** Estimated label bounding box in screen pixels. */
const LABEL_WIDTH = 80;
const LABEL_HEIGHT = 20;

export interface LabelCandidate {
  x: number;
  y: number;
  severity: string;
  timestamp: string;
  id: string;
}

interface PlacedLabel {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Check if two rectangles overlap. */
function rectsOverlap(a: PlacedLabel, b: PlacedLabel): boolean {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

/**
 * Determine which labels should be visible.
 * Sorts by severity (higher first), then by timestamp (newer first).
 * Greedily places labels, skipping those that overlap already-placed ones.
 *
 * @returns Set of IDs whose labels should be displayed.
 */
export function deconflictLabels(points: LabelCandidate[]): Set<string> {
  const visible = new Set<string>();
  const placed: PlacedLabel[] = [];

  // Sort: severity descending, then timestamp descending (newer first)
  const sorted = [...points].sort((a, b) => {
    const sevA = SEVERITY_RANK[(a.severity as Severity) ?? 'info'] ?? 0;
    const sevB = SEVERITY_RANK[(b.severity as Severity) ?? 'info'] ?? 0;
    if (sevB !== sevA) return sevB - sevA;

    // Newer timestamps first
    const tA = new Date(a.timestamp).getTime();
    const tB = new Date(b.timestamp).getTime();
    return tB - tA;
  });

  for (const point of sorted) {
    // Label positioned to the right and slightly above the marker
    const candidate: PlacedLabel = {
      x: point.x + 6,
      y: point.y - LABEL_HEIGHT / 2,
      w: LABEL_WIDTH,
      h: LABEL_HEIGHT,
    };

    let overlaps = false;
    for (const existing of placed) {
      if (rectsOverlap(candidate, existing)) {
        overlaps = true;
        break;
      }
    }

    if (!overlaps) {
      visible.add(point.id);
      placed.push(candidate);
    }
  }

  return visible;
}

/** Debounced label deconfliction state. */
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let currentVisibleSet = new Set<string>();

/**
 * Schedule a debounced label deconfliction pass.
 * Returns the current visible set immediately; updates asynchronously.
 *
 * @param points Screen-projected point positions
 * @param onUpdate Callback when the visible set changes
 */
export function scheduleDeconfliction(
  points: LabelCandidate[],
  onUpdate?: (visibleIds: Set<string>) => void,
): Set<string> {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    const newSet = deconflictLabels(points);

    // Only notify if the set changed and a callback was provided
    if (onUpdate && (newSet.size !== currentVisibleSet.size || !setsEqual(newSet, currentVisibleSet))) {
      currentVisibleSet = newSet;
      onUpdate(newSet);
    } else {
      currentVisibleSet = newSet;
    }
  }, 100);

  return currentVisibleSet;
}

/** Check if two sets contain the same elements. */
function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

/** Clean up any pending debounce timer. */
export function destroyLabelDeconfliction(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  currentVisibleSet = new Set();
}
